// src/modules/app-views/services/pages/Ai/components/SearchedService.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Flex, Tag, Typography, Divider, Descriptions, Collapse, Grid, theme } from 'antd';
import {
  EditOutlined,
  DollarOutlined,
  TagsOutlined,
  AuditOutlined,
  SafetyOutlined,
  ClockCircleOutlined,
  ToolOutlined,
  BookOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  BulbOutlined,
  FileTextOutlined,
  PlayCircleOutlined,
  GlobalOutlined,
  SearchOutlined,
  TagOutlined,
} from '@ant-design/icons';
import translateX from 'utils/translateX';
import TypingText from 'modules/app-views/products/pages/Ai/components/TypingText';
import SimpleAvatar from 'components/SimpleComponents/SimpleAvatar';
import { formatPrice, buildNaturalIntro } from '../helpers';

const { Text } = Typography;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
/**
 * SearchedService - Displays the AI cataloged service details
 */
const SearchedService = ({
  message,
  typingProgress,
  introTypingComplete,
  catalogedServiceId,
  chatContainerRef,
  showAvatar = true,
  creditsInfo = {},
  onIntroComplete,
  onEditService,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = theme.useToken();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const screens = Grid.useBreakpoint();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isMobile = !screens.md;

  const { service, meta, creditsDebit, alreadyExists } = message;
  const tp = typingProgress;
  const ca = service?.costAnalysis;
  const sp = service?.suggestedPricing;
  const fd = service?.fiscalData;
  const tr = service?.taxRetentions;
  const cl = service?.classification;
  const proc = service?.procedure;
  const refs = service?.references;

  // Generate natural language intro
  const naturalIntro = buildNaturalIntro(service, alreadyExists);

  // Check if sections have data (!! ensures boolean, prevents rendering "0")
  const hasCostAnalysis = !!(ca && (ca.estimatedCost || ca.laborCost || ca.materialsCost || ca.benchTime));
  const hasSuggestedPricing = !!(sp && (sp.suggestedPrice || sp.priceRangeMin || sp.profitMargin));
  const hasFiscalData = !!(fd && (fd.cnae || fd.lc116Code || fd.issTaxRate !== undefined));
  const hasTaxRetentions = !!(tr && (
    tr.pisTaxRate !== undefined ||
    tr.cofinsTaxRate !== undefined ||
    tr.csllTaxRate !== undefined ||
    tr.irrfTaxRate !== undefined ||
    tr.inssTaxRate !== undefined
  ));
  const hasClassification = !!(cl && (cl.category || cl.subcategory));
  const hasProcedure = !!(proc && (proc.overview || proc.steps?.length || proc.tools));
  const hasTools = !!(proc?.tools?.physical?.length > 0 || proc?.tools?.software?.length > 0);
  const hasReferences = !!(refs && (refs.videos?.length || refs.websites?.length || refs.searchTerms?.length));

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ width: '100%' }}>
      {/* Natural language intro with typing effect */}
      <div style={{ marginBottom: 20, lineHeight: 1.6, fontSize: 15 }}>
        <TypingText
          text={naturalIntro}
          speed={10}
          onComplete={onIntroComplete}
          scrollContainerRef={chatContainerRef}
          style={{ color: 'var(--ant-color-text)' }}
        />
      </div>

      {/* Service Header: Avatar + Title - shows after intro */}
      {introTypingComplete && tp.name && (
        <Flex
          vertical={isMobile}
          align={isMobile ? 'center' : 'flex-start'}
          gap={16}
          style={{ marginBottom: 20 }}
          className="fade-in"
        >
          {/* Service avatar with deterministic colors (conditional based on config) */}
          {showAvatar && (
            <SimpleAvatar
              name={service?.shortName || service?.name}
              size={80}
              shape="square"
              backColor={service?.backColor}
              foreColor={service?.foreColor}
              style={{ borderRadius: 12, flexShrink: 0 }}
            />
          )}

          <Flex vertical justify="center" style={{ textAlign: isMobile ? 'center' : 'left' }}>
            <div
              style={{
                fontSize: isMobile ? 18 : 20,
                fontWeight: 600,
                lineHeight: 1.3,
                marginBottom: 4,
              }}
            >
              {service?.shortName || service?.name}
            </div>
            {service?.shortName && service?.name && service?.shortName !== service?.name && (
              <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.4 }}>
                {service.name}
              </Text>
            )}
            {service?.description && (
              <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.4, marginTop: 4 }}>
                {service.description}
              </Text>
            )}

            {/* Suggested price highlight */}
            {sp?.suggestedPrice && (
              <Flex align="center" justify={isMobile ? 'center' : 'flex-start'} gap={8} style={{ marginTop: 12 }}>
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 700,
                    color: token.colorPrimary,
                  }}
                >
                  {formatPrice(sp.suggestedPrice, true)}
                </div>
                {!alreadyExists && (
                  <Tag color="blue" style={{ margin: 0 }}>
                    {translateX('suggested_price')}
                  </Tag>
                )}
              </Flex>
            )}
          </Flex>
        </Flex>
      )}

      {/* Cost Analysis Section */}
      {introTypingComplete && tp.costs && hasCostAnalysis && (
        <Collapse
          className="fade-in collapsed-content-fit"
          defaultActiveKey={['cost-analysis']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'cost-analysis',
              showArrow: false,
              label: (
                <Flex align="center" gap={8}>
                  <DollarOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('cost_analysis')}</span>
                </Flex>
              ),
              children: (
                <Descriptions
                  bordered
                  size="small"
                  column={1}
                  layout={isMobile ? 'vertical' : 'horizontal'}
                  styles={{ label: { fontWeight: 500 } }}
                >
                  {ca.estimatedCost !== undefined && (
                    <Descriptions.Item label={translateX('estimated_cost')}>
                      {formatPrice(ca.estimatedCost, true)}
                    </Descriptions.Item>
                  )}
                  {ca.laborCost !== undefined && (
                    <Descriptions.Item label={translateX('labor_cost')}>
                      {formatPrice(ca.laborCost, true)}
                    </Descriptions.Item>
                  )}
                  {ca.materialsCost !== undefined && (
                    <Descriptions.Item label={translateX('materials_cost')}>
                      {formatPrice(ca.materialsCost, true)}
                    </Descriptions.Item>
                  )}
                  {ca.overheadCost !== undefined && (
                    <Descriptions.Item label={translateX('overhead_cost')}>
                      {formatPrice(ca.overheadCost, true)}
                    </Descriptions.Item>
                  )}
                  {ca.riskMargin !== undefined && (
                    <Descriptions.Item label={translateX('risk_margin')}>
                      {formatPrice(ca.riskMargin, true)}
                    </Descriptions.Item>
                  )}
                  {ca.benchTime && (
                    <Descriptions.Item label={translateX('bench_time')}>
                      {ca.benchTime}
                    </Descriptions.Item>
                  )}
                  {ca.activeRepairTime && (
                    <Descriptions.Item label={translateX('active_repair_time')}>
                      {ca.activeRepairTime}
                    </Descriptions.Item>
                  )}
                  {ca.costNotes && (
                    <Descriptions.Item label={translateX('cost_notes')}>
                      {ca.costNotes}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              ),
            },
          ]}
        />
      )}

      {/* Suggested Pricing Section */}
      {introTypingComplete && tp.pricing && hasSuggestedPricing && (
        <Collapse
          className="fade-in collapsed-content-fit"
          defaultActiveKey={['suggested-pricing']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'suggested-pricing',
              showArrow: false,
              label: (
                <Flex align="center" gap={8}>
                  <ClockCircleOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('suggested_pricing')}</span>
                </Flex>
              ),
              children: (
                <Descriptions
                  bordered
                  size="small"
                  column={1}
                  layout={isMobile ? 'vertical' : 'horizontal'}
                  styles={{ label: { fontWeight: 500 } }}
                >
                  {sp.suggestedPrice !== undefined && (
                    <Descriptions.Item label={translateX('suggested_price')}>
                      {formatPrice(sp.suggestedPrice, true)}
                    </Descriptions.Item>
                  )}
                  {(sp.priceRangeMin !== undefined || sp.priceRangeMax !== undefined) && (
                    <Descriptions.Item label={translateX('price_range')}>
                      {formatPrice(sp.priceRangeMin, true)} - {formatPrice(sp.priceRangeMax, true)}
                    </Descriptions.Item>
                  )}
                  {sp.profitMargin !== undefined && (
                    <Descriptions.Item label={translateX('profit_margin')}>
                      {typeof sp.profitMargin === 'number' ? `${sp.profitMargin}%` : sp.profitMargin}
                    </Descriptions.Item>
                  )}
                  {sp.priceNotes && (
                    <Descriptions.Item label={translateX('price_notes')}>
                      {sp.priceNotes}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              ),
            },
          ]}
        />
      )}

      {/* Fiscal Data Section */}
      {introTypingComplete && tp.fiscal && hasFiscalData && (
        <Collapse
          className="fade-in collapsed-content-fit"
          defaultActiveKey={['fiscal-data']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'fiscal-data',
              showArrow: false,
              label: (
                <Flex align="center" gap={8}>
                  <AuditOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('fiscal_data')}</span>
                </Flex>
              ),
              children: (
                <Descriptions
                  bordered
                  size="small"
                  column={1}
                  layout={isMobile ? 'vertical' : 'horizontal'}
                  styles={{ label: { fontWeight: 500 } }}
                >
                  {fd.cnae && (
                    <Descriptions.Item label="CNAE">
                      {fd.cnae}
                      {fd.cnaeDescription && (
                        <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                          {fd.cnaeDescription}
                        </Text>
                      )}
                    </Descriptions.Item>
                  )}
                  {fd.lc116Code && (
                    <Descriptions.Item label="LC 116">
                      {fd.lc116Code}
                      {fd.lc116Description && (
                        <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                          {fd.lc116Description}
                        </Text>
                      )}
                    </Descriptions.Item>
                  )}
                  {fd.issTaxRate !== undefined && (
                    <Descriptions.Item label="ISS">
                      {fd.issTaxRate}%
                    </Descriptions.Item>
                  )}
                </Descriptions>
              ),
            },
          ]}
        />
      )}

      {/* Tax Retentions Section */}
      {introTypingComplete && tp.fiscal && hasTaxRetentions && (
        <Collapse
          className="fade-in collapsed-content-fit"
          defaultActiveKey={['tax-retentions']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'tax-retentions',
              showArrow: false,
              label: (
                <Flex align="center" gap={8}>
                  <SafetyOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('tax_retentions')}</span>
                </Flex>
              ),
              children: (
                <Descriptions
                  bordered
                  size="small"
                  column={1}
                  layout={isMobile ? 'vertical' : 'horizontal'}
                  styles={{ label: { fontWeight: 500 } }}
                >
                  {tr.pisTaxRate !== undefined && (
                    <Descriptions.Item label="PIS">{tr.pisTaxRate}%</Descriptions.Item>
                  )}
                  {tr.cofinsTaxRate !== undefined && (
                    <Descriptions.Item label="COFINS">{tr.cofinsTaxRate}%</Descriptions.Item>
                  )}
                  {tr.csllTaxRate !== undefined && (
                    <Descriptions.Item label="CSLL">{tr.csllTaxRate}%</Descriptions.Item>
                  )}
                  {tr.irrfTaxRate !== undefined && (
                    <Descriptions.Item label="IRRF">{tr.irrfTaxRate}%</Descriptions.Item>
                  )}
                  {tr.inssTaxRate !== undefined && (
                    <Descriptions.Item label="INSS">{tr.inssTaxRate}%</Descriptions.Item>
                  )}
                </Descriptions>
              ),
            },
          ]}
        />
      )}

      {/* Classification Section */}
      {introTypingComplete && tp.fiscal && hasClassification && (
        <Collapse
          className="fade-in collapsed-content-fit"
          defaultActiveKey={['classification']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'classification',
              showArrow: false,
              label: (
                <Flex align="center" gap={8}>
                  <TagsOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('classification')}</span>
                </Flex>
              ),
              children: (
                <Descriptions
                  bordered
                  size="small"
                  column={1}
                  layout={isMobile ? 'vertical' : 'horizontal'}
                  styles={{ label: { fontWeight: 500 } }}
                >
                  {cl.category && (
                    <Descriptions.Item label={translateX('category')}>
                      {cl.category}
                    </Descriptions.Item>
                  )}
                  {cl.subcategory && (
                    <Descriptions.Item label={translateX('subcategory')}>
                      {cl.subcategory}
                    </Descriptions.Item>
                  )}
                  {service?.unitOfMeasure && (
                    <Descriptions.Item label={translateX('unit')}>
                      {service.unitOfMeasure.unit}
                      {service.unitOfMeasure.description && ` - ${service.unitOfMeasure.description}`}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              ),
            },
          ]}
        />
      )}

      {/* Procedure - Overview */}
      {introTypingComplete && tp.procedure && hasProcedure && proc.overview && (
        <Collapse
          className="fade-in collapsed-content-fit"
          defaultActiveKey={['overview']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'overview',
              showArrow: false,
              label: (
                <Flex align="center" gap={8}>
                  <FileTextOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('procedure_overview')}</span>
                </Flex>
              ),
              children: (
                <div style={{ padding: 16, lineHeight: 1.6, color: 'var(--ant-color-text-secondary)' }}>
                  {proc.overview}
                </div>
              ),
            },
          ]}
        />
      )}

      {/* Procedure - Tools */}
      {introTypingComplete && tp.procedure && hasProcedure && hasTools && (
        <Collapse
          className="fade-in collapsed-content-fit"
          defaultActiveKey={['tools']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'tools',
              showArrow: false,
              label: (
                <Flex align="center" gap={8}>
                  <ToolOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('tools_needed')}</span>
                </Flex>
              ),
              children: (
                <div style={{ padding: 16 }}>
                  {proc.tools.physical?.length > 0 && (
                    <Descriptions
                      bordered
                      size="small"
                      column={1}
                      layout={isMobile ? 'vertical' : 'horizontal'}
                      styles={{ label: { fontWeight: 500 } }}
                      style={{ marginBottom: proc.tools.software?.length > 0 ? 16 : 0 }}
                    >
                      <Descriptions.Item label={translateX('physical_tools')}>
                        <Flex gap={4} wrap="wrap">
                          {proc.tools.physical.map((tool, i) => (
                            <Tag key={i} title={tool.description}>{tool.name}</Tag>
                          ))}
                        </Flex>
                      </Descriptions.Item>
                    </Descriptions>
                  )}
                  {proc.tools.software?.length > 0 && (
                    <Descriptions
                      bordered
                      size="small"
                      column={1}
                      layout={isMobile ? 'vertical' : 'horizontal'}
                      styles={{ label: { fontWeight: 500 } }}
                    >
                      <Descriptions.Item label={translateX('software_tools')}>
                        <Flex gap={4} wrap="wrap">
                          {proc.tools.software.map((tool, i) => (
                            <Tag key={i} color="blue" title={tool.description}>{tool.name}</Tag>
                          ))}
                        </Flex>
                      </Descriptions.Item>
                    </Descriptions>
                  )}
                </div>
              ),
            },
          ]}
        />
      )}

      {/* Procedure - Pre-Checklist */}
      {introTypingComplete && tp.procedure && proc?.preChecklist?.length > 0 && (
        <Collapse
          className="fade-in collapsed-content-fit"
          defaultActiveKey={['pre-checklist']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'pre-checklist',
              showArrow: false,
              label: (
                <Flex align="center" gap={8}>
                  <CheckCircleOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('pre_service_checklist')}</span>
                </Flex>
              ),
              children: (
                <div style={{ padding: 16 }}>
                  {proc.preChecklist.map((item, i) => (
                    <div
                      key={i}
                      style={{
                        marginBottom: i < proc.preChecklist.length - 1 ? 12 : 0,
                        padding: '10px 12px',
                        borderRadius: 8,
                        backgroundColor: 'var(--ant-color-fill-quaternary)',
                      }}
                    >
                      <Text strong style={{ fontSize: 13 }}>
                        {i + 1}. {item.item}
                      </Text>
                      {item.detail && (
                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 2 }}>
                          {item.detail}
                        </Text>
                      )}
                    </div>
                  ))}
                </div>
              ),
            },
          ]}
        />
      )}

      {/* Procedure - Steps */}
      {introTypingComplete && tp.procedure && proc?.steps?.length > 0 && (
        <Collapse
          className="fade-in collapsed-content-fit"
          defaultActiveKey={['steps']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'steps',
              showArrow: false,
              label: (
                <Flex align="center" gap={8}>
                  <BookOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('step_by_step')}</span>
                </Flex>
              ),
              children: (
                <div style={{ padding: 16 }}>
                  {proc.steps.map((step, i) => (
                    <div
                      key={i}
                      style={{
                        marginBottom: i < proc.steps.length - 1 ? 12 : 0,
                        padding: '10px 12px',
                        borderRadius: 8,
                        backgroundColor: 'var(--ant-color-fill-quaternary)',
                      }}
                    >
                      <Flex justify="space-between" align="center" style={{ marginBottom: 4 }}>
                        <Text strong style={{ fontSize: 13 }}>
                          {i + 1}. {step.phase}
                        </Text>
                        {step.duration && (
                          <Tag color="default" style={{ margin: 0, fontSize: 11 }}>
                            {step.duration}
                          </Tag>
                        )}
                      </Flex>
                      {step.description && (
                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                          {step.description}
                        </Text>
                      )}
                      {step.actions?.length > 0 && (
                        <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
                          {step.actions.map((action, j) => (
                            <li key={j} style={{ fontSize: 12, color: 'var(--ant-color-text-secondary)' }}>
                              {action}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              ),
            },
          ]}
        />
      )}

      {/* Procedure - Common Problems */}
      {introTypingComplete && tp.procedure && proc?.commonProblems?.length > 0 && (
        <Collapse
          className="fade-in collapsed-content-fit"
          defaultActiveKey={['common-problems']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'common-problems',
              showArrow: false,
              label: (
                <Flex align="center" gap={8}>
                  <ExclamationCircleOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('common_problems')}</span>
                </Flex>
              ),
              children: (
                <div style={{ padding: 16 }}>
                  <Collapse
                    accordion
                    size="small"
                    items={proc.commonProblems.map((item, i) => ({
                      key: String(i),
                      label: (
                        <Text strong style={{ fontSize: 13 }}>
                          {i + 1}. {item.problem}
                        </Text>
                      ),
                      children: (
                        <div style={{ padding: 16 }}>
                          <div style={{ marginBottom: 8 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              <strong>{translateX('cause')}:</strong>
                            </Text>
                            <Text type="secondary" style={{ fontSize: 12, display: 'block', paddingLeft: 8 }}>
                              {item.cause}
                            </Text>
                          </div>
                          <div>
                            <Text style={{ fontSize: 12 }}>
                              <strong>{translateX('solution')}:</strong>
                            </Text>
                            <Text style={{ fontSize: 12, display: 'block', paddingLeft: 8 }}>
                              {item.solution}
                            </Text>
                          </div>
                        </div>
                      ),
                    }))}
                  />
                </div>
              ),
            },
          ]}
        />
      )}

      {/* Procedure - Tips */}
      {introTypingComplete && tp.procedure && proc?.tips?.length > 0 && (
        <Collapse
          className="fade-in collapsed-content-fit"
          defaultActiveKey={['tips']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'tips',
              showArrow: false,
              label: (
                <Flex align="center" gap={8}>
                  <BulbOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('business_tips')}</span>
                </Flex>
              ),
              children: (
                <div style={{ padding: 16 }}>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {proc.tips.map((tip, i) => (
                      <li key={i} style={{ marginBottom: 4, fontSize: 13 }}>{tip}</li>
                    ))}
                  </ul>
                </div>
              ),
            },
          ]}
        />
      )}

      {/* References - Videos */}
      {introTypingComplete && tp.references && refs?.videos?.length > 0 && (
        <Collapse
          className="fade-in collapsed-content-fit"
          defaultActiveKey={['videos']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'videos',
              showArrow: false,
              label: (
                <Flex align="center" gap={8}>
                  <PlayCircleOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('recommended_videos')}</span>
                </Flex>
              ),
              children: (
                <div style={{ padding: 16 }}>
                  {refs.videos.map((video, i) => (
                    <div
                      key={i}
                      style={{
                        marginBottom: i < refs.videos.length - 1 ? 12 : 0,
                        padding: '10px 12px',
                        borderRadius: 8,
                        backgroundColor: 'var(--ant-color-fill-quaternary)',
                      }}
                    >
                      {video.url ? (
                        <a href={video.url} target="_blank" rel="noopener noreferrer">
                          <Text strong style={{ fontSize: 13 }}>{video.title}</Text>
                        </a>
                      ) : (
                        <Text strong style={{ fontSize: 13 }}>{video.title}</Text>
                      )}
                      {video.channel && (
                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 2 }}>
                          {video.channel}
                        </Text>
                      )}
                      {video.topic && (
                        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 2 }}>
                          {video.topic}
                        </Text>
                      )}
                    </div>
                  ))}
                </div>
              ),
            },
          ]}
        />
      )}

      {/* References - Websites */}
      {introTypingComplete && tp.references && refs?.websites?.length > 0 && (
        <Collapse
          className="fade-in collapsed-content-fit"
          defaultActiveKey={['websites']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'websites',
              showArrow: false,
              label: (
                <Flex align="center" gap={8}>
                  <GlobalOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('reference_websites')}</span>
                </Flex>
              ),
              children: (
                <div style={{ padding: 16 }}>
                  {refs.websites.map((site, i) => (
                    <div
                      key={i}
                      style={{
                        marginBottom: i < refs.websites.length - 1 ? 12 : 0,
                        padding: '10px 12px',
                        borderRadius: 8,
                        backgroundColor: 'var(--ant-color-fill-quaternary)',
                      }}
                    >
                      {site.url ? (
                        <a href={site.url} target="_blank" rel="noopener noreferrer">
                          <Text strong style={{ fontSize: 13 }}>{site.name}</Text>
                        </a>
                      ) : (
                        <Text strong style={{ fontSize: 13 }}>{site.name}</Text>
                      )}
                      {site.description && (
                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 2 }}>
                          {site.description}
                        </Text>
                      )}
                    </div>
                  ))}
                </div>
              ),
            },
          ]}
        />
      )}

      {/* References - Search Terms */}
      {introTypingComplete && tp.references && refs?.searchTerms?.length > 0 && (
        <Collapse
          className="fade-in collapsed-content-fit"
          defaultActiveKey={['search-terms']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'search-terms',
              showArrow: false,
              label: (
                <Flex align="center" gap={8}>
                  <SearchOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('search_terms')}</span>
                </Flex>
              ),
              children: (
                <div style={{ padding: 16 }}>
                  <Flex gap={4} wrap="wrap">
                    {refs.searchTerms.map((term, i) => (
                      <Tag key={i} color="processing">{term}</Tag>
                    ))}
                  </Flex>
                </div>
              ),
            },
          ]}
        />
      )}

      {/* Meta info and Edit link */}
      {introTypingComplete && tp.meta && meta && (
        <div className="fade-in" style={{ marginTop: 16 }}>
          <Divider style={{ margin: '12px 0' }} />
          <Flex
            vertical={isMobile}
            justify={isMobile ? 'center' : 'space-between'}
            align="center"
            wrap="wrap"
            gap={isMobile ? 12 : 16}
          >
            {/* Meta stats */}
            <Flex gap={isMobile ? 16 : 24} wrap="wrap" justify={isMobile ? 'center' : 'flex-start'}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <span style={{ fontWeight: 500 }}>{translateX('model')}:</span> {meta.model}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <span style={{ fontWeight: 500 }}>Tokens:</span>{' '}
                {meta.totalTokens?.toLocaleString()}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <span style={{ fontWeight: 500 }}>{translateX('time')}:</span>{' '}
                {(meta.responseTimeMs / 1000).toFixed(1)}s
              </Text>
              {creditsDebit?.debited !== undefined && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <span style={{ fontWeight: 500 }}>{translateX('credits_debited')}:</span>{' '}
                  {creditsDebit.debited}
                </Text>
              )}
              {creditsInfo?.availableCredits !== undefined && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <span style={{ fontWeight: 500 }}>{translateX('current_balance')}:</span>{' '}
                  {creditsInfo.availableCredits}
                </Text>
              )}
            </Flex>

            {/* Edit Service link */}
            {tp.editLink && catalogedServiceId && (
              <Flex gap={16} align="center" justify={isMobile ? 'center' : 'flex-end'}>
                <a
                  onClick={() => onEditService(catalogedServiceId)}
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    marginTop: isMobile ? 16 : 0,
                  }}
                  className="fade-in"
                >
                  <EditOutlined />
                  {translateX('edit_service')}
                </a>
              </Flex>
            )}
          </Flex>
        </div>
      )}

      {/* Progressive phase indicator - shows next section being "processed" */}
      {introTypingComplete && renderRevealPhase(tp)}
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SearchedService;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Sections reveal order mapped to phase messages.
 * Each entry: what section must appear BEFORE this phase text shows,
 * and what the phase text says while "processing" the NEXT section.
 */
const REVEAL_PHASES = [
  { after: null,         phaseKey: 'ai_phase_analyzing_service',       icon: SearchOutlined },
  { after: 'name',       phaseKey: 'ai_phase_calculating_costs',       icon: DollarOutlined },
  { after: 'costs',      phaseKey: 'ai_phase_searching_prices',        icon: TagOutlined },
  { after: 'pricing',    phaseKey: 'ai_phase_verifying_fiscal',        icon: AuditOutlined },
  { after: 'fiscal',     phaseKey: 'ai_phase_researching_procedures',  icon: BookOutlined },
  { after: 'procedure',  phaseKey: 'ai_phase_compiling_references',    icon: GlobalOutlined },
  { after: 'references', phaseKey: 'ai_phase_finalizing',              icon: CheckCircleOutlined },
];

/**
 * Render the current reveal phase indicator with shimmer effect.
 * Shows what's "being processed" next, disappears when all sections are revealed.
 */
function renderRevealPhase(tp) {
  // Find the current phase: the first entry whose "after" section is revealed
  // but whose NEXT section is NOT yet revealed
  let currentPhase = null;

  for (const phase of REVEAL_PHASES) {
    // Check if the prerequisite section is revealed (or null for the first)
    const prerequisiteMet = phase.after === null || tp[phase.after];
    if (!prerequisiteMet) break;
    currentPhase = phase;
  }

  // If meta is revealed, all done - no more phase indicator
  if (!currentPhase || tp.meta) return null;

  const PhaseIcon = currentPhase.icon;

  return (
    <div className="fade-in" style={{ marginTop: 16, marginBottom: 8 }}>
      <span className="shimmer-text" style={{ fontSize: 14 }}>
        <PhaseIcon style={{ fontSize: 14 }} />
        <span>{translateX(currentPhase.phaseKey)}...</span>
      </span>
    </div>
  );
}
